import useApi from "@/hooks/useApi";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import useTeams from "@/hooks/useTeams";
import useRules from "@/hooks/useRules";
import useChatWindows from "@/hooks/useChatWindows";
import DeploymentTabs from "./DeploymentTabs";
import {
  LoadingSpinner,
  SecondaryBlueButton,
  PrimaryBlueButton,
  FormValidator,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import AuthService from "@/services/AuthService";
import useToastAlert from "@/hooks/useToastAlert";
import useAsync from "@/hooks/useAsync";
import rawEmbedScript from "@/pages/Messaging/WebMessenger/components/DeployModal/EmbedScript.html?raw";
import FormGroup from "@/components/FormGroup";
import Select from "@/components/Select";
import { Rule } from "@/pages/Messaging/Rules/types";
import { ChatWindowValue } from "@/pages/Messaging/Rules/ValueForms/ChatWindowRuleForm";
import RuleManager from "@/pages/Messaging/Rules";

export interface Deployment {
  id: number;
  deploymentId: string;
  displayName: string;
  teamId: number;
  siteId: number;
}

export default function Deployments() {
  const api = useApi();
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("General");
  const [selectedTeam, setSelectedTeam] = useState<number>(0);
  const [chatAvailable, setChatAvailable] = useState<boolean>(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedChatWindow, setSelectedChatWindow] = useState<string>("");
  const [hasExistingRule, setHasExistingRule] = useState<boolean>(false);
  const toastAlert = useToastAlert();

  const { teams, getTeamNameById } = useTeams();
  const { rules, add: addRule, update: updateRule } = useRules();
  const { chatWindows } = useChatWindows();

  const fetchDeployment = useCallback(async () => {
    setLoading(true);
    const resp = await api.messaging.get(`Deployment`);
    const deployments = await resp.json();
    // Use first deployment as the active one
    if (deployments && deployments.length > 0) {
      setDeployment(deployments[0]);
      setSelectedTeam(deployments[0].teamId);
    }
    setLoading(false);
  }, [api.messaging]);

  useEffect(() => {
    fetchDeployment();
  }, [fetchDeployment]);

  // Check if deployment already has a chat window rule
  useEffect(() => {
    const existingRule = rules.find(r =>
      r.trigger === "determine-chat-window"
      // TODO: Add deployment context filter when available
    );

    if (existingRule) {
      setHasExistingRule(true);
      setSelectedChatWindow((existingRule.value as ChatWindowValue).chatWindow);
    }
  }, [rules]);

  const embedScript = useMemo(() => {
    if (!deployment || loading) {
      return "";
    }
    return rawEmbedScript
      .replace("{{ siteId }}", AuthService.profile.siteId.toString())
      .replace("{{ deploymentId }}", deployment.deploymentId.toString())
      .replace(
        "{{ scriptSrc }}",
        `${import.meta.env.VITE_MESSAGING_CLIENT_URL!}/messenger/embed.js`
      );
  }, [deployment, loading]);

  const linkUrl = useMemo(() => {
    if (!deployment || loading) {
      return "";
    }

    return `${import.meta.env
      .VITE_MESSAGING_CLIENT_URL!}/example_embed.html?siteId=${AuthService.profile.siteId.toString()}&deploymentId=${
      deployment.deploymentId
    }`;
  }, [deployment, loading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!deployment) return;
    const { name, value } = e.target;
    setDeployment({ ...deployment, [name]: value });
  };

  const handleTeamChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (!deployment) return;
    setSelectedTeam(Number(event.target.value));
    setDeployment({ ...deployment, teamId: Number(event.target.value) });
  };

  const handleChatWindowChange = async (chatWindowName: string) => {
    setSelectedChatWindow(chatWindowName);

    if (!hasExistingRule) {
      // Auto-generate simple rule for beginners
      const newRule: Rule = {
        trigger: "determine-chat-window",
        priority: 1,
        expression: [], // Empty expression means "always applies"
        value: {
          chatWindow: chatWindowName
        } as ChatWindowValue
      };

      try {
        await addRule(newRule);
        setHasExistingRule(true);
      } catch (error) {
        console.error("Failed to create chat window rule:", error);
      }
    } else {
      // Update existing rule
      const existingRule = rules.find(r => r.trigger === "determine-chat-window");
      if (existingRule) {
        const updatedRule = {
          ...existingRule,
          value: { chatWindow: chatWindowName } as ChatWindowValue
        };

        try {
          await updateRule(updatedRule);
        } catch (error) {
          console.error("Failed to update chat window rule:", error);
        }
      }
    }
  };

  async function handleSubmit() {
    if (!deployment) return;

    setErrors({});
    const newErrors: Record<string, string> = {};

    if (
      deployment.deploymentId === "" ||
      /^\s*$/.test(deployment.deploymentId)
    ) {
      newErrors["DeploymentId"] = "Deployment Id required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) {
      return;
    }

    const response = await api.messaging.put(
      `Deployment/${deployment.id}`,
      deployment
    );

    if (response.ok) {
      toastAlert.displayToast(
        Severity.Success,
        "Deployment updated successfully"
      );
    }

    if (response.status === 400) {
      const errorResponse = await response.json();
      if (typeof errorResponse.errors === "object") {
        setErrors(errorResponse.errors);
      }
      toastAlert.displayToast(Severity.Error, errorResponse);
    }

    if (response.status === 401) {
      toastAlert.displayToast(
        Severity.Error,
        "You do not have permission to edit this deployment"
      );
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col">
      <DeploymentTabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
      />

      <div className="bg-white p-6 border-[1px] rounded-2xl shadow-sm w-[750px] self-center">
        <div>
          <div className="flex items-center justify-between">
            <div className="font-medium text-md pb-1">Deployment Settings</div>
            <div className="text-slate-500 text-xs">
              Site ID: {AuthService.profile.siteId}
            </div>
          </div>
          <div className="text-slate-600 text-sm pb-2">
            Apply your deployment configurations
          </div>
          <hr className="mt-2 mb-4 border-gray-200" />
        </div>

        {selectedTab === "General" && (
          <div>
            {/* Team */}
            <div className="w-full mb-6">
              <div className="mb-2">
                <label className="block text-sm font-normal text-black mb-1">
                  Team
                </label>
                <div className="text-sm text-slate-600 mb-2">
                  Select the team that will manage this deployment.
                </div>
              </div>
              <Select
                options={teams.reduce((acc, team) => {
                  acc[team.id.toString()] = team.name;
                  return acc;
                }, {} as Record<string, string>)}
                value={selectedTeam.toString()}
                onChange={(value) =>
                  handleTeamChange({
                    target: { value }
                  } as React.ChangeEvent<HTMLSelectElement>)
                }
              />
            </div>

            {/* Chat Window Picker - Smart Dropdown */}
            <div className="w-full mb-6">
              <div className="mb-2">
                <label className="block text-sm font-normal text-black mb-1">
                  Chat Window
                </label>
              </div>
              <div className="text-sm text-slate-600 mb-2">
                Select which chat window to use for this deployment.
              </div>

              <Select
                options={chatWindows?.reduce((acc, window) => {
                  acc[window.name] = window.name;
                  return acc;
                }, {} as Record<string, string>) || {}}
                value={selectedChatWindow}
                onChange={(value) => handleChatWindowChange(value)}
              />


              <div className="mt-2">
                <a
                  href="/chat_window"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline text-xs cursor-pointer"
                >
                  Create new chat window
                </a>
              </div>
            </div>

            {/* Chat Availability - Placeholder */}
            <div className="w-full mb-6">
              <div className="flex items-center gap-3 mb-2">
                <label className="block text-sm font-normal text-black">
                  Chat Availability
                </label>
                <Toggle
                  toggled={chatAvailable}
                  changeToggle={() => setChatAvailable(!chatAvailable)}
                />
              </div>
              <div className="text-sm text-slate-600 mb-2">
                By default, chat is available 24/7. Switch off to set your
                custom hours.
              </div>
            </div>

            {/* Save Button */}
            <div className="py-4 flex mt-4">
              <div className="ml-auto flex">
                <PrimaryBlueButton
                  label="Save Changes"
                  onClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        )}
        {selectedTab === "Rules" && (
          <div>
            <div className="mb-4">
              <div className="font-normal text-sm pb-2">Rule Management</div>
              <div className="text-sm text-slate-600 mb-3">
                Control chat behavior, routing, and appearance with rules. Settings from the General tab automatically create rules here, which you can enhance with advanced conditions.
              </div>
            </div>
            <RuleManager />
          </div>
        )}
        {selectedTab === "Deployment Code" && (
          <div>
            <div className="mb-4">
              <div className="font-normal text-sm pb-2">Embed Script</div>
              <div className="text-sm text-slate-600 mb-3">
                Paste this code snippet just before the &lt;/body&gt; tag to
                each page of your website where you want to display the chat
                window.
              </div>
              <textarea
                className="input w-full h-96 font-mono text-sm"
                readOnly
                value={embedScript}
              />
              <div className="text-sm text-slate-600 mt-3">
                Go to your website where you&apos;ve deployed the chat window
                code. The chat window should be visible. Any additional changes
                to the chat window design and deployment settings will be
                reflected automatically without additional redeployment.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
